/**
 * The attendance archive, for the teacher who recorded it.
 *
 * The roster is a day-of tool: it opens on today, it is a list of controls, and it forgets
 * yesterday the moment the date rolls over. Everything a teacher records there — every
 * check-in, every handover, every absence and its reason — has until now been readable only
 * from the admin student page, so "was she in Chinese G3 last Tuesday?" meant asking an
 * admin to look it up.
 *
 * THE SHAPE
 *
 * A teacher's question is one class, one day at a time, and it is usually asked on a phone
 * held one-handed at pickup. So the narrow screen gets a card per session date — tally in
 * the header, names folded away until the card is opened, the same status chips and the
 * same wording as the roster the teacher already reads.
 *
 * At a desk it gets the admin archive's table instead, from the same
 * `AttendanceHistoryTable` that page renders, still grouped by day. The day grouping is the
 * part that belongs to this surface; the row is not, and rebuilding it here would be two
 * renderings of one record waiting to disagree. No export — that stays with the admin.
 *
 * WHAT IT CAN SHOW
 *
 * Exactly the rows the teacher's own attendance grant already covers — the programs in
 * `staff_users.program_ids` and the classes in `virtual_class_keys` — enforced in the
 * database by `staff_attendance_history`, not here. The tabs below are a menu, not a
 * boundary: they are built from the same grant so nobody is offered a band they would get
 * an empty answer for, but hand-editing the URL to a class you do not hold returns nothing
 * because the RPC re-states the RLS policy's own test.
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { StaffLayout } from '../StaffLayout';
import { ScrollTabs } from '../components/ScrollTabs';
import {
  AttendanceHistoryTable,
  type AttendanceHistorySection,
} from '../../components/attendance/AttendanceHistoryTable';
import { AttendanceRecordCard } from '../../components/attendance/AttendanceRecordCard';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import {
  HISTORY_BAND_LABEL,
  type HistoryBand,
} from '../../hooks/useAttendanceHistory';
import {
  STAFF_HISTORY_PAGE_SIZE,
  useStaffAttendanceHistory,
  useTeacherHistoryScope,
  type StaffHistoryFilters,
} from '../useStaffAttendanceHistory';
import { isSubjectBand } from '../attendanceHistoryScope';
import {
  attendanceClassLabel,
  groupAttendanceByDay,
  type AttendanceDay,
  type AttendanceHistoryRow,
} from '../../utils/attendanceHistory';
import { formatGradeLabel } from '../../utils/rosterGrouping';
import { virtualClassKey, virtualGradeLabel } from '../virtualClasses';
import { formatWeekdayDateDisplay, getTodayDateStringInBusinessTZ } from '../../utils/date';
import { shiftDateString } from '../../utils/schedule';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
  virtual_class_keys?: string[] | null;
}

interface Props {
  staffUser: StaffUser;
}

/**
 * How far back the archive opens, and the ranges offered.
 *
 * Presses, not date pickers, for the ordinary case: a native date input on a phone is three
 * taps and a spinner each, and "the last two weeks" is nearly every question a teacher
 * brings here. The custom range is still there for the one that isn't.
 */
const RANGE_PRESETS = [
  { value: '7', days: 7, label: '7 days' },
  { value: '30', days: 30, label: '30 days' },
  { value: '90', days: 90, label: '90 days' },
] as const;

const CUSTOM_RANGE = 'custom';
const DEFAULT_RANGE = '30';
const RANGE_VALUES = [...RANGE_PRESETS.map(preset => preset.value), CUSTOM_RANGE];

/** Query-string value for "every grade in this subject". */
const ALL_GRADES = 'all';

/** How long the search box waits before asking the database. */
const SEARCH_DEBOUNCE_MS = 300;

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const message = (e as { message: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return 'Something went wrong.';
}

function pillClass(active: boolean): string {
  return `flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
    active
      ? 'bg-blue-600 border-blue-600 text-white'
      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
}

/**
 * The context line under a name: which class, and — outside the subject classes — which
 * grade and school.
 *
 * Suppressed entirely when it would say the same thing on every row. Inside Chinese G3 the
 * class is the heading, the grade is the class, and every child is from a different school,
 * so all three lines would be noise on a phone.
 */
function rowContext(row: AttendanceHistoryRow, band: HistoryBand, oneClass: boolean): string | null {
  const classLabel = attendanceClassLabel(row.virtualClassKey, row.programName);

  if (isSubjectBand(band)) {
    return oneClass ? null : classLabel;
  }

  const grade = row.studentGrade?.trim();
  return [classLabel, grade ? formatGradeLabel(grade) : null, row.studentSchool?.trim() || null]
    .filter(Boolean)
    .join(' · ') || null;
}

/**
 * How a day went, in three numbers.
 *
 * The reason a folded card is worth folding: "did anyone miss Tuesday" is answerable
 * without opening anything. It sits on the table's day heading too, where the rows below it
 * may well be a page away.
 */
function DayTally({ day }: { day: AttendanceDay }) {
  const present = day.tallies.checked_in + day.tallies.picked_up;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
      {present > 0 && <span className="text-green-700">{present} present</span>}
      {day.tallies.absent > 0 && <span className="text-red-600">{day.tallies.absent} absent</span>}
      {day.tallies.not_arrived > 0 && <span>{day.tallies.not_arrived} no record</span>}
    </span>
  );
}

export function StaffHistory({ staffUser }: Props) {
  const today = getTodayDateStringInBusinessTZ();
  const { scope, isLoading: scopeLoading } = useTeacherHistoryScope(staffUser);

  // The band is the first choice and belongs in the URL, so a teacher can send "look at
  // Chinese G3 last week" to a colleague and have it open there.
  const [band, setBand] = useUrlTabState<HistoryBand>(
    'band',
    scope.bands,
    scope.bands[0] ?? 'afterschool',
  );

  const subjectGrades = isSubjectBand(band) ? scope.gradesBySubject[band] : [];
  // Not memoised: `useUrlTabState` only reads this list to validate the URL's value, so a
  // fresh array each render costs nothing and cannot go stale against the band above it.
  const gradeValues = [ALL_GRADES, ...subjectGrades];
  const [grade, setGrade] = useUrlTabState<string>('grade', gradeValues, ALL_GRADES);

  const [range, setRange] = useUrlTabState<string>('range', RANGE_VALUES, DEFAULT_RANGE);
  const [customFrom, setCustomFrom] = useState(() => shiftDateString(today, -30));
  const [customTo, setCustomTo] = useState(today);

  const [absencesOnly, setAbsencesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // A press per keystroke would be a query per keystroke, on a connection that is often a
  // phone in a school yard.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const preset = RANGE_PRESETS.find(option => option.value === range);
  const from = preset ? shiftDateString(today, -(preset.days - 1)) : customFrom;
  const to = preset ? today : customTo;
  const rangeInvalid = from > to;

  const filters: StaffHistoryFilters = useMemo(
    () => ({
      from,
      to,
      band,
      // A grade names one of the fourteen classes; "all" is the whole subject. Outside the
      // subject bands there is no such key, and sending one would return nothing.
      virtualClassKey:
        isSubjectBand(band) && grade !== ALL_GRADES ? virtualClassKey(band, grade) : null,
      status: absencesOnly ? 'absent' : 'all',
      search: debouncedSearch,
    }),
    [from, to, band, grade, absencesOnly, debouncedSearch],
  );

  const hasScope = scope.bands.length > 0;
  const {
    data,
    error,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useStaffAttendanceHistory(filters, hasScope && !rangeInvalid);

  const rows = useMemo(
    () => (data?.pages ?? []).flatMap(page => page.rows),
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;
  // Grouped after the pages are concatenated, so a day split across a page boundary is one
  // card that grows rather than two cards that read as two Tuesdays.
  const days = useMemo(() => groupAttendanceByDay(rows), [rows]);
  /** The same days, as headings for the table. */
  const tableSections: AttendanceHistorySection[] = useMemo(
    () => days.map(day => ({
      key: day.date,
      label: formatWeekdayDateDisplay(day.date),
      meta: <DayTally day={day} />,
      rows: day.rows,
    })),
    [days],
  );

  const searching = debouncedSearch.trim().length > 0;

  // Which day cards are open. A card the teacher pressed wins; everything else follows the
  // default — the newest day, which is what they came for, plus every day while searching,
  // where a fold would hide the one row that matched.
  const [toggledDays, setToggledDays] = useState<Record<string, boolean>>({});
  /** Which table row has its detail open. The table's own affordance, not the cards'. */
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  useEffect(() => {
    setToggledDays({});
    setExpandedRow(null);
  }, [band, grade, from, to, absencesOnly, debouncedSearch]);

  function isOpen(date: string, index: number): boolean {
    return toggledDays[date] ?? (searching || index === 0);
  }

  const selection = isSubjectBand(band)
    ? `${HISTORY_BAND_LABEL[band]}${grade === ALL_GRADES ? '' : ` ${virtualGradeLabel(grade)}`}`
    : HISTORY_BAND_LABEL[band];

  const oneClass = isSubjectBand(band) && grade !== ALL_GRADES;

  return (
    <StaffLayout staffUser={staffUser}>
      <div className="mb-3">
        <h1 className="text-lg sm:text-xl font-bold text-gray-900">Attendance history</h1>
        <p className="text-xs sm:text-sm text-gray-500">
          {selection} · {formatWeekdayDateDisplay(from)} – {formatWeekdayDateDisplay(to)}
        </p>
      </div>

      {scopeLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : !hasScope ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">
            Your account is not assigned to any programs or classes, so there is no
            attendance to show.
          </p>
        </div>
      ) : (
        <>
          {/* The same first choice as the menu — After School, Enrichment, Camps, Chinese,
              Math — narrowed to the ones this teacher holds. A single band draws no strip:
              one tab is a label, not a choice. */}
          {scope.bands.length > 1 && (
            <ScrollTabs
              label="classes"
              activeKey={band}
              className="-mx-4 sm:-mx-6 lg:-mx-10 mb-3 border-b border-gray-200"
            >
              {scope.bands.map(option => {
                const isActive = option === band;
                return (
                  <button
                    key={option}
                    type="button"
                    data-tab-key={option}
                    data-band={option}
                    onClick={() => setBand(option)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex-shrink-0 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {HISTORY_BAND_LABEL[option]}
                  </button>
                );
              })}
            </ScrollTabs>
          )}

          {/* Chinese and Math split by grade, because there the grade IS the class. "All
              grades" leads, since a teacher holding several is usually asking about a child
              rather than about one room. */}
          {isSubjectBand(band) && subjectGrades.length > 1 && (
            <ScrollTabs
              label="grades"
              activeKey={grade}
              className="-mx-4 sm:-mx-6 lg:-mx-10 mb-3 border-b border-gray-200"
            >
              {gradeValues.map(value => {
                const isActive = value === grade;
                return (
                  <button
                    key={value}
                    type="button"
                    data-tab-key={value}
                    data-grade={value}
                    onClick={() => setGrade(value)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex-shrink-0 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {value === ALL_GRADES ? 'All grades' : virtualGradeLabel(value)}
                  </button>
                );
              })}
            </ScrollTabs>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3">
            {RANGE_PRESETS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setRange(option.value)}
                aria-pressed={range === option.value}
                className={pillClass(range === option.value)}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRange(CUSTOM_RANGE)}
              aria-pressed={range === CUSTOM_RANGE}
              className={pillClass(range === CUSTOM_RANGE)}
            >
              Custom
            </button>

            <label className="ml-auto flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={absencesOnly}
                onChange={e => setAbsencesOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Absences only
            </label>
          </div>

          {range === CUSTOM_RANGE && (
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <label className="text-xs text-gray-500">
                From
                <input
                  type="date"
                  value={customFrom}
                  max={today}
                  onChange={e => setCustomFrom(e.target.value)}
                  className="block mt-1 px-2.5 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="text-xs text-gray-500">
                To
                <input
                  type="date"
                  value={customTo}
                  max={today}
                  onChange={e => setCustomTo(e.target.value)}
                  className="block mt-1 px-2.5 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
          )}

          {/* Searched in the database over the whole range, not over the rows on screen: the
              child a parent is asking about may last have been here in June. */}
          <div className="relative mb-3">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              inputMode="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Find a student"
              aria-label="Find a student"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              /* 16px on phones: anything smaller makes iOS Safari zoom the page in on
                 focus, which leaves the teacher scrolled sideways. */
              className="w-full pl-9 pr-10 py-2.5 text-base sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <p className="mb-2 text-xs text-gray-500">
            {rangeInvalid
              ? 'The From date is after the To date.'
              : isLoading
                ? 'Loading…'
                : `${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
            {isFetching && !isLoading && <span className="text-gray-400"> · refreshing</span>}
          </p>

          {error ? (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4">
              <p className="text-sm text-red-700">
                Could not load attendance history: {errorMessage(error)}
              </p>
            </div>
          ) : rangeInvalid ? null : isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />)}
            </div>
          ) : days.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <p className="text-gray-500 text-sm">
                {searching
                  ? `No record in this range matches "${debouncedSearch.trim()}".`
                  : absencesOnly
                    ? 'No absences were recorded in this range.'
                    : 'Nothing was recorded for this class in this range.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* At a desk, the admin archive's table — same rows, same columns, grouped by
                  day instead of by school. Below 1024px the cards take over. */}
              <div className="hidden lg:block bg-white border border-gray-200 rounded-xl overflow-hidden" data-layout="table">
                <AttendanceHistoryTable
                  sections={tableSections}
                  showDate={false}
                  expandedId={expandedRow}
                  onToggleExpanded={setExpandedRow}
                />
              </div>

              <div className="lg:hidden space-y-3" data-layout="cards">
                {days.map((day, index) => {
                  const open = isOpen(day.date, index);
                  return (
                    <section
                      key={day.date}
                      data-session-date={day.date}
                      className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setToggledDays(prev => ({ ...prev, [day.date]: !open }))
                        }
                        aria-expanded={open}
                        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 text-left hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-sm font-semibold text-gray-900">
                          {formatWeekdayDateDisplay(day.date)}
                        </span>
                        <DayTally day={day} />
                        <ChevronDown
                          className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                            open ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {open && (
                        <ul className="divide-y divide-gray-100">
                          {day.rows.map(row => (
                            <AttendanceRecordCard
                              key={row.id}
                              row={row}
                              context={rowContext(row, band, oneClass)}
                            />
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>

              {hasNextPage && (
                <button
                  type="button"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="w-full py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isFetchingNextPage
                    ? 'Loading…'
                    : `Load ${Math.min(STAFF_HISTORY_PAGE_SIZE, total - rows.length)} more`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </StaffLayout>
  );
}
